import React, { useEffect, useMemo, useState } from 'react';
import { collection, limit, onSnapshot, orderBy, query, where, type Timestamp } from 'firebase/firestore';
import { Area, AreaChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { AlertTriangle, ArrowLeft } from 'lucide-react';
import { Navigate } from 'react-router-dom';
import AppHeader from '../components/ui/AppHeader';
import { useAuth } from '../utils/AuthContext';
import { db } from '../utils/firebase';
import {
  buildApiUsageChart,
  type ApiUsageEvent,
  type ApiUsageGroupBy,
} from '../utils/apiUsageChart';
import '../components/pm/pm-shared.css';
import './OperationsHub.css';
import './ApiUsagePage.css';

const USAGE_LIMIT = 2000;

const SERIES_COLORS = [
  '#3d6b4f',
  '#c4a574',
  '#5c7c8a',
  '#8c6a4a',
  '#6b8f71',
  '#a65d57',
  '#7d6b8a',
  '#4f6d5a',
  '#9aa39a',
];

const COMPANY_GROUPS: { id: ApiUsageGroupBy; label: string }[] = [
  { id: 'user', label: 'User' },
  { id: 'crmGroup', label: 'CRM group' },
  { id: 'module', label: 'CRM module' },
  { id: 'action', label: 'Action' },
];

const DRILL_GROUPS: { id: ApiUsageGroupBy; label: string }[] = [
  { id: 'module', label: 'CRM module' },
  { id: 'action', label: 'Action' },
  { id: 'decision', label: 'Allow vs deny' },
];

type RangeId = '7d' | '30d';

function rangeWindow(range: RangeId, now = new Date()): { from: Date; to: Date } {
  const to = now;
  const from = new Date(now);
  from.setUTCDate(from.getUTCDate() - (range === '30d' ? 30 : 7));
  return { from, to };
}

function formatWhen(ts?: Date | null): string {
  if (!ts || Number.isNaN(ts.getTime())) return '—';
  return ts.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

function moduleLabel(row: ApiUsageEvent): string {
  const raw = (row.agentId || row.endpoint || 'unknown').replace(/Agent$/i, '');
  return raw
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[_./-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/\b\w/g, (c) => c.toUpperCase()) || 'Unknown';
}

type TooltipRow = { name?: string; value?: number; color?: string };

function UsageTooltip({
  active,
  payload,
  label,
}: {
  active?: boolean;
  payload?: TooltipRow[];
  label?: string;
}) {
  if (!active || !payload?.length) return null;
  const rows = payload.filter((entry) => Number(entry.value) > 0);
  if (!rows.length) return null;
  return (
    <div className="api-usage-tooltip">
      <div className="api-usage-tooltip-day">{label}</div>
      {rows.map((entry) => (
        <div key={entry.name} className="api-usage-tooltip-row">
          <span className="api-usage-dot" style={{ background: entry.color }} />
          <span>{entry.name}</span>
          <strong>{entry.value}</strong>
        </div>
      ))}
    </div>
  );
}

const ApiUsagePage: React.FC = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [rows, setRows] = useState<ApiUsageEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [range, setRange] = useState<RangeId>('7d');
  const [groupBy, setGroupBy] = useState<ApiUsageGroupBy>('user');
  const [drillUid, setDrillUid] = useState<string | null>(null);

  useEffect(() => {
    if (!isAdmin) return undefined;
    setRows(null);
    setError(null);
    const { from } = rangeWindow(range);
    const usageQuery = query(
      collection(db, 'crmApiUsage'),
      where('ts', '>=', from),
      orderBy('ts', 'desc'),
      limit(USAGE_LIMIT),
    );
    return onSnapshot(
      usageQuery,
      (snap) => {
        setRows(
          snap.docs.map((docSnap) => {
            const data = docSnap.data() as Omit<ApiUsageEvent, 'id' | 'ts'> & { ts?: Timestamp | null };
            return {
              id: docSnap.id,
              uid: data.uid,
              displayName: data.displayName,
              crmGroup: data.crmGroup,
              crmRole: data.crmRole,
              action: data.action,
              endpoint: data.endpoint,
              agentId: data.agentId,
              decision: data.decision,
              reason: data.reason,
              keyId: data.keyId,
              ts: data.ts?.toDate?.() ?? null,
            };
          }),
        );
        setError(null);
      },
      (err) => {
        console.error('[ApiUsage]', err);
        setError(err.message || 'Could not load API usage');
        setRows([]);
      },
    );
  }, [isAdmin, range]);

  const usageWindow = useMemo(() => rangeWindow(range), [range]);
  const chart = useMemo(
    () =>
      buildApiUsageChart({
        rows: rows || [],
        groupBy,
        drillUid,
        from: usageWindow.from,
        to: usageWindow.to,
      }),
    [rows, groupBy, drillUid, usageWindow.from, usageWindow.to],
  );

  const groupOptions = drillUid ? DRILL_GROUPS : COMPANY_GROUPS;
  const calls = chart.totals.reduce((sum, row) => sum + row.calls, 0);
  const denies = chart.totals.reduce((sum, row) => sum + row.denies, 0);
  const capped = (rows?.length || 0) >= USAGE_LIMIT;

  const openPerson = (uid: string) => {
    setDrillUid(uid);
    setGroupBy('module');
  };

  const closePerson = () => {
    setDrillUid(null);
    setGroupBy('user');
  };

  if (!isAdmin) return <Navigate to="/settings/account" replace />;

  return (
    <div className="ops-hub api-usage-page">
      <AppHeader />
      <div className="ops-hub-toolbar">
        <div className="ops-hub-toolbar-left">
          <h1 className="ops-hub-title">API usage</h1>
          <div className="ops-hub-stats">
            <span className="ops-stat">{calls.toLocaleString()} calls</span>
            <span className={`ops-stat${denies ? ' ops-stat-overdue' : ''}`}>
              {denies.toLocaleString()} denied
            </span>
          </div>
        </div>
        <div className="ops-hub-toolbar-right">
          <div className="ops-hub-views" role="group" aria-label="Time range">
            <button
              type="button"
              className={`ops-view-btn${range === '7d' ? ' active' : ''}`}
              onClick={() => setRange('7d')}
            >
              7 days
            </button>
            <button
              type="button"
              className={`ops-view-btn${range === '30d' ? ' active' : ''}`}
              onClick={() => setRange('30d')}
            >
              30 days
            </button>
          </div>
        </div>
      </div>

      <div className="api-usage-body">
        {error && (
          <p className="api-usage-error">
            <AlertTriangle size={14} />
            {error}
          </p>
        )}
        {rows == null && !error && (
          <div className="ops-hub-loading">
            <div className="ops-hub-loading-bar" />
          </div>
        )}

        {rows && (
          <>
            <section className="api-usage-card">
              <div className="api-usage-card-head">
                <div>
                  {chart.drillName && (
                    <button type="button" className="api-usage-back" onClick={closePerson}>
                      <ArrowLeft size={14} />
                      All usage
                    </button>
                  )}
                  <h2>{chart.drillName ? `${chart.drillName}'s calls` : 'Calls per day'}</h2>
                </div>
                <label className="api-usage-group">
                  Group by
                  <select
                    value={groupBy}
                    onChange={(event) => setGroupBy(event.target.value as ApiUsageGroupBy)}
                  >
                    {groupOptions.map((option) => (
                      <option key={option.id} value={option.id}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </label>
              </div>

              <div className="api-usage-chart">
                <ResponsiveContainer width="100%" height={280}>
                  <AreaChart data={chart.points} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid stroke="var(--pm-border-subtle)" vertical={false} />
                    <XAxis
                      dataKey="label"
                      tick={{ fontSize: 11, fill: 'var(--pm-text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <YAxis
                      allowDecimals={false}
                      width={36}
                      tick={{ fontSize: 11, fill: 'var(--pm-text-muted)' }}
                      axisLine={false}
                      tickLine={false}
                    />
                    <Tooltip content={<UsageTooltip />} />
                    {chart.series.map((series, index) => (
                      <Area
                        key={series.key}
                        type="monotone"
                        dataKey={series.key}
                        name={series.label}
                        stackId="calls"
                        stroke={SERIES_COLORS[index % SERIES_COLORS.length]}
                        fill={SERIES_COLORS[index % SERIES_COLORS.length]}
                        fillOpacity={0.88}
                      />
                    ))}
                  </AreaChart>
                </ResponsiveContainer>
              </div>

              <div className="api-usage-legend">
                {chart.series.map((series, index) => {
                  const color = SERIES_COLORS[index % SERIES_COLORS.length];
                  const canDrill = groupBy === 'user' && series.id !== '__other__';
                  const body = (
                    <>
                      <span className="api-usage-dot" style={{ background: color }} />
                      {series.label}
                    </>
                  );
                  return canDrill ? (
                    <button key={series.key} type="button" className="api-usage-legend-btn" onClick={() => openPerson(series.id)}>
                      {body}
                    </button>
                  ) : (
                    <span key={series.key} className="api-usage-legend-item">
                      {body}
                    </span>
                  );
                })}
              </div>
              {capped && (
                <p className="api-usage-cap">
                  Showing the latest {USAGE_LIMIT.toLocaleString()} calls. Older calls in this range are not on the chart.
                </p>
              )}
            </section>

            <section className="api-usage-card">
              <h2>{chart.drillName ? `${chart.drillName} by ${groupOptions.find((g) => g.id === groupBy)?.label || 'group'}` : 'Breakdown'}</h2>
              {chart.totals.length === 0 ? (
                <p className="api-usage-empty">No API calls in this range.</p>
              ) : (
                <table className="api-usage-table api-usage-table-nums">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Calls</th>
                      <th>Denied</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chart.totals.map((total) => {
                      const canDrill = groupBy === 'user';
                      return (
                        <tr key={total.id}>
                          <td>
                            {canDrill ? (
                              <button type="button" className="api-usage-name" onClick={() => openPerson(total.id)}>
                                {total.label}
                              </button>
                            ) : (
                              total.label
                            )}
                          </td>
                          <td>{total.calls.toLocaleString()}</td>
                          <td>{total.denies.toLocaleString()}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </section>

            <section className="api-usage-card">
              <h2>Recent denied</h2>
              {chart.denied.length === 0 ? (
                <p className="api-usage-empty">No auth denials in this view.</p>
              ) : (
                <table className="api-usage-table">
                  <thead>
                    <tr>
                      <th>When</th>
                      <th>User</th>
                      <th>Module</th>
                      <th>Action</th>
                      <th>Reason</th>
                    </tr>
                  </thead>
                  <tbody>
                    {chart.denied.slice(0, 12).map((row) => (
                      <tr key={row.id}>
                        <td>{formatWhen(row.ts)}</td>
                        <td>{row.displayName || row.uid || 'Unknown'}</td>
                        <td>{moduleLabel(row)}</td>
                        <td>{row.action || '—'}</td>
                        <td>{row.reason || '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ApiUsagePage;
